import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, IndianRupee, CalendarDays, BarChart3, GraduationCap, Receipt, Zap } from 'lucide-react';
import SEOHead from './SEOHead';
import LandingFooter from './LandingFooter';
import ContactForm from './ContactForm';
import ContactInfo from './ContactInfo';
import { clientAPI } from '../services';
import './landing.css';

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';
const TEAL = '#0ea98b';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'DigiHR — HRMS Software India',
  url: 'https://digihr.in/',
  description: 'Cloud HRMS for Indian businesses: GPS attendance app, payroll software with PF/ESI/PT/TDS, leave management, performance appraisals, L&D and expense claims.',
  about: { '@type': 'SoftwareApplication', name: 'DigiHR', applicationCategory: 'BusinessApplication' },
};

const FEATURES = [
  { icon: MapPin, iconColor: '#0ea98b', bg: '#e0f7f3', title: 'GPS Attendance', anchor: 'gps-attendance', page: '/gps-attendance-app', desc: 'Real-time location-verified clock-ins. Works offline, syncs when connected. Perfect for field teams and remote employees.' },
  { icon: IndianRupee, iconColor: ACCENT, bg: '#e8efff', title: 'Payroll Automation', anchor: 'payroll', page: '/payroll-software-india', desc: 'Auto-calculate salaries, deductions, PF, ESI, TDS and PT. Generate payslips in one click with full compliance.' },
  { icon: CalendarDays, iconColor: '#d97706', bg: '#fef3c7', title: 'Leave Management', anchor: 'leave', page: '/leave-management-system', desc: 'Configurable leave policies, instant approval flows, holiday calendars and automated balance carry-overs.' },
  { icon: BarChart3, iconColor: '#db2777', bg: '#fce7f3', title: 'Performance Appraisals', anchor: 'performance', desc: 'Goal setting, 360° feedback, continuous reviews and calibration tools to grow your people faster.' },
  { icon: GraduationCap, iconColor: '#16a34a', bg: '#f0fdf4', title: 'Learning & Development', anchor: 'learning', desc: 'Build courses, assign training programmes, track completion and link L&D to performance outcomes.' },
  { icon: Receipt, iconColor: '#ea580c', bg: '#fff7ed', title: 'Expense Claims', anchor: 'expenses', desc: 'Submit, approve and reimburse expense claims with receipt uploads, policy checks and instant audit trails.' },
];

const TESTIMONIALS = [
  { stars: 5, quote: '"DigiHR\'s GPS attendance completely solved our remote team tracking problem. Payroll now takes 20 minutes instead of 2 days."', name: 'Ravi Kulkarni', role: 'HR Director, TechNova Pune', initials: 'RK', color: '#6366f1' },
  { stars: 5, quote: '"The expense claim module alone saves our finance team 15+ hours a month. The policy enforcement is automatic — no more policy violations."', name: 'Nisha Sharma', role: 'CFO, Axiom Labs', initials: 'NS', color: TEAL },
  { stars: 5, quote: '"We evaluated 6 HRMS platforms. DigiHR was the only one that had everything we needed without overcomplicating it. Onboarding took one week."', name: 'Ananya Patel', role: 'People Ops Lead, Skyrise Corp', initials: 'AP', color: '#f59e0b' },
];

const Home = () => {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    clientAPI.getPublicLogos()
      .then(r => setClients(r?.data || []))
      .catch(() => {});
  }, []);

  return (
  <>
    <SEOHead
      title="DigiHR — HRMS Software India | GPS Attendance App & Payroll"
      description="All-in-one HRMS software for Indian businesses. GPS attendance app with selfie check-in, payroll with PF, ESI, PT & TDS, leave, performance and expenses. Book a free demo."
      keywords="HRMS software India, HR software India, GPS attendance app, attendance management system, payroll software India, leave management system, field employee tracking app, HR software for small business India"
      canonical="/"
      schema={homeSchema}
    />

    {/* HERO */}
    <section style={{ padding: '80px 5% 60px', background: 'linear-gradient(160deg,#f0f4ff 0%,#ffffff 50%,#f0fff9 100%)', position: 'relative', overflow: 'hidden' }}>
      <div className="hero-grid-bg" />
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center', position: 'relative' }} className="hero-inner-grid">
        {/* Left */}
        <div className="lp-anim-1">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8efff', border: '1px solid #d0e0ff', color: ACCENT, fontSize: 12, fontWeight: 500, padding: '5px 12px', borderRadius: 100, marginBottom: 20 }}>
            <span className="lp-dot-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, display: 'inline-block' }} />
            India's All-in-One HRMS Platform
          </div>
          <h1 style={{ ...serif, fontSize: 'clamp(36px,4.5vw,56px)', lineHeight: 1.1, letterSpacing: '-.03em', color: '#0d1117', marginBottom: 20 }}>
            HR that works<br />as hard as <em style={{ fontStyle: 'italic', color: ACCENT }}>your team</em>
          </h1>
          <p style={{ fontSize: 17, color: '#3a3f4a', lineHeight: 1.65, marginBottom: 32, maxWidth: 480 }}>
            DigiHR brings GPS attendance, payroll automation, leave management, performance appraisals and more into one seamless platform — built for Indian businesses.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '13px 28px', borderRadius: 20, background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 500, textDecoration: 'none', boxShadow: '0 2px 8px rgba(26,95,255,.3)', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1248d6'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.transform = 'none'; }}>
              Book a free Demo
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
            <Link to="/features" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '13px 28px', borderRadius: 20, background: 'transparent', color: '#3a3f4a', border: '1px solid #e4e7ed', fontSize: 15, fontWeight: 500, textDecoration: 'none', transition: 'all .2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f6f7f9'; e.currentTarget.style.borderColor = '#c8cdd8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e4e7ed'; }}>
              See all features
            </Link>
          </div>
          <div style={{ marginTop: 40, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, color: '#717888' }}>
            <div style={{ display: 'flex' }}>
              {[['#6366f1','R'],['#0ea98b','P'],['#f59e0b','A'],['#f43f5e','S']].map(([bg,l],i) => (
                <span key={l} style={{ width: 30, height: 30, borderRadius: '50%', border: '2px solid #fff', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#fff', marginLeft: i ? -8 : 0 }}>{l}</span>
              ))}
            </div>
            <span>Trusted by <strong style={{ color: '#0d1117' }}>50+ companies</strong> · <strong style={{ color: '#0d1117' }}>5,000+</strong> employees managed</span>
          </div>
        </div>

        {/* Right - Dashboard mockup */}
        <div className="lp-anim-2 hero-visual-grid" style={{ position: 'relative' }}>
          <div style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 20, boxShadow: '0 12px 40px rgba(13,17,23,.12),0 4px 12px rgba(13,17,23,.06)', overflow: 'hidden' }}>
            <div style={{ background: '#0d1117', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 5 }}>
                {['#f43f5e','#f59e0b','#22c55e'].map(c => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />)}
              </div>
              <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginLeft: 4 }}>DigiHR — Employee Dashboard</span>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                {[['Present Today','142','↑ 96.5%','#0ea98b'],['On Leave','5','3.4%','#717888'],['Payroll Due','₹ 4.2L','June 30','#f43f5e']].map(([lbl,val,tag,tc]) => (
                  <div key={lbl} style={{ background: '#f6f7f9', borderRadius: 12, padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, color: '#717888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{lbl}</div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#0d1117' }}>{val}</div>
                    <div style={{ fontSize: 10, marginTop: 2, color: tc }}>{tag}</div>
                  </div>
                ))}
              </div>
              {[['#6366f1','PR','Priya Reddy','Engineering','Present','#dcfce7','#15803d'],
                ['#0ea98b','AK','Arjun Kumar','Marketing','On Leave','#fef3c7','#92400e'],
                ['#f43f5e','SM','Sneha Malhotra','HR','Remote','#e8efff',ACCENT]].map(([ac,ini,name,dept,badge,bgb,cb]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 6, fontSize: 12, marginBottom: 6, background: '#f6f7f9' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: ac, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, color: '#fff', flexShrink: 0 }}>{ini}</div>
                  <div style={{ fontWeight: 500, flex: 1 }}>{name}</div>
                  <div style={{ color: '#717888', fontSize: 11 }}>{dept}</div>
                  <span style={{ padding: '2px 8px', borderRadius: 100, fontSize: 10, fontWeight: 500, background: bgb, color: cb }}>{badge}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Float cards */}
          <div style={{ position: 'absolute', bottom: -20, left: -30, background: '#fff', border: '1px solid #e4e7ed', borderRadius: 12, boxShadow: '0 4px 16px rgba(13,17,23,.08)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 6, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MapPin size={18} color="#15803d" strokeWidth={2} /></div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>GPS Check-in</div>
              <div style={{ fontSize: 11, color: '#717888' }}>Verified · 9:02 AM</div>
            </div>
          </div>
          <div style={{ position: 'absolute', top: -20, right: -20, background: '#fff', border: '1px solid #e4e7ed', borderRadius: 12, boxShadow: '0 4px 16px rgba(13,17,23,.08)', padding: '12px 16px', textAlign: 'center', fontSize: 12 }}>
            <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}><Zap size={22} color="#f59e0b" strokeWidth={2} /></div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Payslip Ready</div>
            <div style={{ fontSize: 11, color: '#717888' }}>Auto-generated</div>
          </div>
        </div>
      </div>
    </section>

    {/* LOGOS BAND */}
    {clients.length > 0 && (
      <div style={{ padding: '40px 5%', borderTop: '1px solid #e4e7ed', borderBottom: '1px solid #e4e7ed', background: '#f6f7f9' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontSize: 12, color: '#717888', textAlign: 'center', marginBottom: 28, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 500 }}>Trusted by teams at</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px 36px', flexWrap: 'wrap' }}>
            {clients.map(c => (
              <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {c.logo?.url
                  ? <img src={c.logo.url} alt={c.name} style={{ height: 40, maxWidth: 120, objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  : null}
                <span style={{ fontFamily: '"DM Serif Display",Georgia,serif', fontSize: 13, color: '#717888', letterSpacing: '-.01em', display: c.logo?.url ? 'none' : 'block', whiteSpace: 'nowrap' }}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}

    {/* FEATURES GRID */}
    <section style={{ padding: '80px 5%' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Features</div>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 16 }}>
            Everything your HR team <em style={{ fontStyle: 'italic', color: ACCENT }}>needs</em>
          </h2>
          <p style={{ fontSize: 17, color: '#3a3f4a', lineHeight: 1.65, maxWidth: 560, margin: '0 auto' }}>From onboarding to exit interviews, DigiHR covers the full employee lifecycle in one beautifully integrated platform.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }} className="feat-grid-resp">
          {FEATURES.map(({ icon: Icon, iconColor, bg, title, desc, anchor, page }) => (
            <div key={title} style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 20, padding: '28px 28px 24px', transition: 'all .22s ease', cursor: 'default' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d0e0ff'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(13,17,23,.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e4e7ed'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}><Icon size={22} color={iconColor} strokeWidth={1.75} /></div>
              <h3 style={{ fontFamily: '"DM Sans",system-ui,sans-serif', fontSize: 16, fontWeight: 600, color: '#0d1117', marginBottom: 8 }}>{title}</h3>
              <p style={{ fontSize: 14, color: '#3a3f4a', lineHeight: 1.6 }}>{desc}</p>
              <Link to={page || `/features#${anchor}`} className="lp-feat-link">Learn more →</Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* HIGHLIGHT - Attendance */}
    <section style={{ padding: '80px 5%', background: '#f6f7f9' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }} className="highlight-grid-resp">
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Attendance &amp; Payroll</div>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 0 }}>
            Attendance that <em style={{ fontStyle: 'italic', color: ACCENT }}>proves presence</em>, payroll that runs itself
          </h2>
          <ul className="lp-check-list">
            {['GPS-verified check-in with selfie capture','Geo-fenced office zones and remote work modes','Automatic attendance-to-payroll sync','PF, ESI, TDS and PT auto-calculation','One-click payslip generation and email delivery'].map(item => (
              <li key={item}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#e0f7f3', color: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0, marginTop: 2 }}>✓</span>
                {item}
              </li>
            ))}
          </ul>
          <Link to="/features#payroll" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 12, background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
            Explore Payroll →
          </Link>
        </div>
        {/* Attendance visual */}
        <div style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 28, boxShadow: '0 12px 40px rgba(13,17,23,.12)', padding: 28, position: 'relative', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Monthly Attendance — June 2026</span>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 100, background: '#dcfce7', color: '#15803d', fontWeight: 500 }}>97.2% rate</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
            {['p','p','p','p','p','h','h','p','p','p','p','a','h','h','p','p','p','p','p','h','h','p','p','p','p','p','h','h','p','p','p','','','',''].map((cls,i) => (
              <div key={i} className={`att-cell${cls ? ' '+cls : ''}`} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 11, color: '#717888' }}>
            {[['#0ea98b','Present'],['#f59e0b','Holiday'],['#f43f5e','Absent']].map(([c,l]) => (
              <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: c, opacity: .8, display: 'inline-block' }} />{l}
              </span>
            ))}
          </div>
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e4e7ed' }}>
            <div style={{ fontSize: 12, color: '#717888', marginBottom: 10 }}>Payroll summary — June</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: '#717888' }}>Gross Payroll</div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>₹ 42,00,000</div>
              </div>
              <span style={{ padding: '2px 10px', borderRadius: 100, fontSize: 12, fontWeight: 500, background: '#dcfce7', color: '#15803d' }}>Ready to process</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* STATS BAND */}
    <div style={{ padding: '60px 5%', background: '#0d1117', color: '#fff' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32, textAlign: 'center' }} className="stats-grid-resp">
        {[['50','+',' Companies trust DigiHR'],['5','K+',' Employees managed'],['99','%',' Payroll accuracy rate'],['9','+',' HR modules in one platform']].map(([n,s,l]) => (
          <div key={l}>
            <div style={{ ...serif, fontSize: 48, color: '#fff', lineHeight: 1 }}>
              {n}<span style={{ color: ACCENT }}>{s}</span>
            </div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,.5)', marginTop: 6 }}>{l}</div>
          </div>
        ))}
      </div>
    </div>

    {/* TESTIMONIALS */}
    <section style={{ padding: '80px 5%' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Testimonials</div>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117' }}>
            Loved by HR teams <em style={{ fontStyle: 'italic', color: ACCENT }}>across India</em>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }} className="testi-grid-resp">
          {TESTIMONIALS.map(({ quote, name, role, initials, color }) => (
            <div key={name} style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 20, padding: 28 }}>
              <div style={{ color: '#f59e0b', fontSize: 14, marginBottom: 12 }}>★★★★★</div>
              <blockquote style={{ fontSize: 14, color: '#3a3f4a', lineHeight: 1.65, marginBottom: 20, fontStyle: 'italic' }}>{quote}</blockquote>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>{initials}</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 12, color: '#717888' }}>{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA BAND */}
    <section style={{ padding: '80px 5%', background: 'linear-gradient(135deg,#0d1117 0%,#1a2540 100%)', textAlign: 'center', color: '#fff', position: 'relative', overflow: 'hidden' }}
      className="cta-band-radial">
      <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,.5)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Get started</div>
        <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#fff', marginBottom: 14 }}>
          Ready to transform<br />your HR operations?
        </h2>
        <p style={{ fontSize: 17, color: 'rgba(255,255,255,.6)', marginBottom: 32 }}>Join 50+ companies already saving time, reducing errors and keeping employees happy with DigiHR.</p>
        <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '14px 32px', borderRadius: 20, background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 500, textDecoration: 'none', boxShadow: '0 2px 8px rgba(26,95,255,.3)' }}>
          Book a free Demo
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </Link>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,.4)', marginTop: 14 }}>Free 30-day trial set up on your demo call · No credit card required</div>
      </div>
    </section>

    {/* CONTACT */}
    <section style={{ padding: '80px 5%', background: '#f6f7f9' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 60, alignItems: 'start' }} className="contact-grid-resp">
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>Get in touch</div>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 12 }}>
            Let's get your HR <em style={{ fontStyle: 'italic', color: ACCENT }}>sorted</em>
          </h2>
          <p style={{ fontSize: 15, color: '#3a3f4a', marginBottom: 32, lineHeight: 1.65 }}>Book a free 30-minute demo tailored to your team size and industry.</p>
          <ContactInfo />
        </div>
        <ContactForm />
      </div>
    </section>

    <LandingFooter />

    <style>{`
      @media(max-width:900px){
        .hero-inner-grid{ grid-template-columns:1fr !important; }
        .hero-visual-grid{ display:none !important; }
        .feat-grid-resp{ grid-template-columns:1fr !important; }
        .highlight-grid-resp{ grid-template-columns:1fr !important; }
        .stats-grid-resp{ grid-template-columns:repeat(2,1fr) !important; }
        .testi-grid-resp{ grid-template-columns:1fr !important; }
        .contact-grid-resp{ grid-template-columns:1fr !important; }
      }
    `}</style>
  </>
  );
};

export default Home;
